import { doorCopy, doorRules, doorSharedCopy, type ProductRow } from '@planner/config';
import { sanitizeInput, type ProductDataFile } from '@planner/shared';
import {
  createDrawingApproval,
  doorCartTotals,
  doorToOrderItem,
  doorsOrderFingerprint,
  drawingApprovalBinding,
  drawingDeclaration,
  historicalSizeChange,
  isDrawingApprovalValid,
  matchStoredDoorLines,
  parseDoorsDesign,
  resolveDoorProduct,
  storedDoorSize,
  validateDoorLineDetails,
  type DrawingApproval,
} from '@planner/shared/doors';
import { Link, useBlocker, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { reviewPresentation } from '../../appdata.js';
import { doorPreviewImage } from '../components/DoorPreview.js';
import { InfoTip } from '../components/InfoTip.js';
import { LockDialog } from '../components/LockDialog.js';
import { ManufacturingSpecDrawing } from '../components/ManufacturingSpecDrawing.js';
import { StepProgress } from '../components/StepProgress.js';
import { EnlargeChip } from '../components/ZoomableDiagram.js';
import { CartApiError, fetchCart, saveDoorCart } from '../lib/api.js';
import { fetchProductCatalogue } from '../lib/catalogue.js';
import { brand, environment, region } from '../lib/env.js';
import { formatPrice } from '../lib/format.js';
import { parseMillimetres } from '../lib/measurements.js';
import { prepareReviewDoor } from '../lib/review.js';
import { useMeasurementsReadiness } from '../lib/useMeasurements.js';
import { useDoorStore } from '../stores/doorStore.js';

export function ReviewStep() {
  const { draft, evaluation } = useMeasurementsReadiness();
  const cart = useDoorStore((state) => state.cart);
  const cartReference = useDoorStore((state) => state.cartReference);
  const initialiseReview = useDoorStore((state) => state.initialiseReview);
  const setReviewInput = useDoorStore((state) => state.setReviewInput);
  const setConfirmedCart = useDoorStore((state) => state.setConfirmedCart);
  const navigate = useNavigate();
  const [catalogue, setCatalogue] = useState<ProductDataFile<ProductRow> | null>(null);
  const [catalogueError, setCatalogueError] = useState<string | null>(null);
  const [cartError, setCartError] = useState<string | null>(null);
  const [savingError, setSavingError] = useState<string | null>(null);
  const [approval, setApproval] = useState<DrawingApproval | null>(null);
  const [saving, setSaving] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [confirmedSizeKey, setConfirmedSizeKey] = useState<string | null>(null);
  const previousName = useRef('');
  const approvalInput = useRef<HTMLInputElement>(null);
  const savingRef = useRef(false);
  const catalogueRequest = useRef(0);
  const cartRequest = useRef(0);
  useBlocker({
    shouldBlockFn: () => savingRef.current,
    enableBeforeUnload: () => savingRef.current,
  });

  const loadCatalogue = useCallback(async () => {
    const request = ++catalogueRequest.current;
    setCatalogue(null);
    setCatalogueError(null);
    try {
      const result = await fetchProductCatalogue();
      if (request === catalogueRequest.current) {
        setCatalogue(result);
      }
    } catch {
      if (request === catalogueRequest.current) {
        setCatalogueError('The catalogue could not be checked. Please retry.');
      }
    }
  }, []);

  const loadCart = useCallback(async () => {
    if (!cartReference) {
      return;
    }
    const request = ++cartRequest.current;
    setCartError(null);
    try {
      const result = await fetchCart(cartReference);
      const current = useDoorStore.getState();
      if (
        request === cartRequest.current &&
        current.cartReference === cartReference &&
        !current.cart
      ) {
        setConfirmedCart(result);
      }
    } catch {
      const current = useDoorStore.getState();
      if (
        request === cartRequest.current &&
        current.cartReference === cartReference &&
        !current.cart
      ) {
        setCartError(
          'Your saved Cart could not be loaded. Please retry; your draft has been kept.',
        );
      }
    }
  }, [cartReference, setConfirmedCart]);

  useEffect(() => {
    void loadCatalogue();
    return () => {
      catalogueRequest.current += 1;
    };
  }, [loadCatalogue]);
  useEffect(() => {
    if (cartReference && !cart) {
      void loadCart();
    }
    return () => {
      cartRequest.current += 1;
    };
  }, [cartReference, cart, loadCart]);
  useEffect(() => {
    if (!draft.inputs.reviewLineId && (!cartReference || cart)) {
      const count = cart ? parseDoorsDesign(cart.design)?.doors.length : 0;
      if (count !== undefined) {
        initialiseReview(crypto.randomUUID(), `Door ${count + 1}`);
      }
    }
  }, [cart, cartReference, draft.inputs.reviewLineId, initialiseReview]);

  const savedCart = useMemo(() => {
    if (!cart) return undefined;
    const design = parseDoorsDesign(cart.design);
    const doors = design?.doors;
    const lineIds = design && matchStoredDoorLines(design, cart.items, cart.doorLineIds);
    const previousDoor = doors?.find((item) => item.lineId === draft.inputs.reviewLineId);
    const previousItem =
      previousDoor && lineIds ? cart.items[lineIds.indexOf(previousDoor.lineId)] : undefined;
    return {
      doors,
      lineIds,
      previous:
        previousDoor && previousItem ? { door: previousDoor, item: previousItem } : undefined,
    };
  }, [cart, draft.inputs.reviewLineId]);
  const prepared = useMemo(
    () =>
      catalogue || savedCart?.previous
        ? prepareReviewDoor(draft, catalogue, savedCart?.previous)
        : null,
    [catalogue, draft, savedCart],
  );
  const door = prepared?.status === 'ready' ? prepared.door : null;
  const binding = door ? drawingApprovalBinding(door) : null;
  const sizeChange =
    door && savedCart?.previous
      ? historicalSizeChange(savedCart.previous.door, savedCart.previous.item, door)
      : undefined;
  const sizeKey =
    sizeChange && door && cart
      ? JSON.stringify([cart.referenceCode, door.lineId, door.measurements, sizeChange])
      : null;
  const sizeConfirmed = !sizeChange || confirmedSizeKey === sizeKey;
  useEffect(() => {
    setApproval(null);
  }, [binding, sizeKey]);
  const approved =
    sizeConfirmed &&
    !!door &&
    !!approval &&
    isDrawingApprovalValid({ ...door, drawingApproval: approval });
  const cartReady = !cartReference || !!cart;
  const orderingUnavailable = environment === 'prod';
  const name = draft.inputs.reviewName ?? draft.inputs.reviewDefaultName ?? 'Door';
  const quantity = draft.inputs.reviewQuantity ?? '1';
  const size = evaluation.size;
  const lockHeight = parseMillimetres(draft.lock.height);
  const quantityNumber = /^[0-9]+$/.test(quantity) ? Number(quantity) : NaN;
  const quantityValid =
    Number.isSafeInteger(quantityNumber) &&
    quantityNumber >= doorRules.quantity.min &&
    quantityNumber <= doorRules.quantity.max;
  const existingDoors = savedCart?.doors;
  const invalidRetained = existingDoors?.filter(
    (item) => item.lineId !== draft.inputs.reviewLineId && !validateDoorLineDetails(item).valid,
  );
  const cartInvalid = !!cart && !savedCart?.lineIds;
  const replacing = existingDoors?.some((item) => item.lineId === draft.inputs.reviewLineId);
  const atCapacity =
    !!existingDoors && existingDoors.length >= doorRules.maxDoorsPerOrder && !replacing;
  const canSave =
    approved &&
    cartReady &&
    !cartInvalid &&
    cart?.status !== 'submitted' &&
    cart?.status !== 'emailed' &&
    !atCapacity &&
    !editingName &&
    !saving &&
    !orderingUnavailable;
  const product =
    catalogue && draft.doorType && size
      ? resolveDoorProduct(catalogue.rows, draft.doorType, draft.frameColour, size)
      : undefined;
  const unitPrice =
    door?.snapshot.unitPrice ??
    (product?.status === 'available' ? product.product.retailPrice : undefined);
  const linePrice =
    unitPrice !== undefined && quantityValid ? unitPrice * quantityNumber : undefined;

  const commitName = () => {
    const normalized = sanitizeInput(name, Number.MAX_SAFE_INTEGER);
    setReviewInput('reviewName', normalized || draft.inputs.reviewDefaultName || 'Door');
    setEditingName(false);
  };
  const changeQuantity = (delta: number) => {
    if (!quantityValid) return;
    const next = Math.min(
      doorRules.quantity.max,
      Math.max(doorRules.quantity.min, quantityNumber + delta),
    );
    setReviewInput('reviewQuantity', String(next));
  };

  const save = async () => {
    if (!door || !approval || !canSave || savingRef.current) {
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setSavingError(null);
    try {
      const unchanged =
        savedCart?.previous &&
        !sizeChange &&
        drawingApprovalBinding(savedCart.previous.door) === drawingApprovalBinding(door);
      const latest = unchanged ? catalogue : await fetchProductCatalogue();
      const checked = prepareReviewDoor(draft, latest, savedCart?.previous);
      if (
        checked.status !== 'ready' ||
        !isDrawingApprovalValid({ ...checked.door, drawingApproval: approval })
      ) {
        setCatalogue(latest);
        setApproval(null);
        setSavingError(
          'The catalogue details have changed. Check the door and approve the drawing again.',
        );
        return;
      }
      const confirmedDoor = { ...checked.door, drawingApproval: approval };
      const oldDesign = cart ? parseDoorsDesign(cart.design) : undefined;
      const oldLineIds =
        cart && oldDesign && matchStoredDoorLines(oldDesign, cart.items, cart.doorLineIds);
      if (cart && (!oldDesign || !oldLineIds)) {
        throw new CartApiError('The saved Cart could not be matched to its doors.', 'invalid_cart');
      }
      const oldDoors = oldDesign?.doors ?? [];
      const editingLineId =
        draft.inputs.editingLineId ||
        (oldDoors.some((item) => item.lineId === door.lineId) ? door.lineId : undefined);
      if (editingLineId && !oldDoors.some((item) => item.lineId === editingLineId)) {
        throw new CartApiError('This door is no longer in the saved Cart.', 'missing_line');
      }
      if (editingLineId && editingLineId !== confirmedDoor.lineId) {
        throw new CartApiError('This door could not be matched to the saved Cart.', 'missing_line');
      }
      if (!editingLineId && oldDoors.length >= doorRules.maxDoorsPerOrder) {
        throw new CartApiError(
          `A Cart can contain at most ${doorRules.maxDoorsPerOrder} doors.`,
          'cart_full',
        );
      }
      const oldItems = new Map(
        oldLineIds ? oldLineIds.map((id, index) => [id, cart?.items[index]] as const) : [],
      );
      const previous = oldDoors.find((item) => item.lineId === editingLineId);
      const previousItem = editingLineId ? oldItems.get(editingLineId) : undefined;
      const sameSpecification =
        previous &&
        !sizeChange &&
        drawingApprovalBinding(previous) === drawingApprovalBinding(confirmedDoor);
      const replacement =
        sameSpecification &&
        previousItem &&
        isDrawingApprovalValid(previous, storedDoorSize(previousItem))
          ? previous
          : confirmedDoor;
      const doors = editingLineId
        ? oldDoors.map((item) => (item.lineId === editingLineId ? replacement : item))
        : [...oldDoors, replacement];
      const items = doors.map((item) => {
        const oldItem = oldItems.get(item.lineId);
        return oldItem && (oldDoors.includes(item) || (sameSpecification && item === replacement))
          ? oldItem
          : doorToOrderItem(item);
      });
      const design = oldDesign
        ? { ...oldDesign, doors }
        : { schema: 'doors' as const, version: doorRules.designVersion, doors };
      const order = await saveDoorCart({
        region: region.code,
        brand: cart?.brand ?? brand.id,
        design,
        items,
        totals: doorCartTotals(items, region),
        ...(cartReference ? { baseReference: cartReference } : {}),
        ...(editingLineId ? { editingLineId } : {}),
        ...(sizeChange
          ? {
              historicalSizeConfirmation: {
                lineId: confirmedDoor.lineId,
                ...sizeChange,
              },
            }
          : {}),
      });
      if (cart) {
        const contentChanged =
          doorsOrderFingerprint(cart.design, cart.items) !== doorsOrderFingerprint(design, items);
        const sameReference = order.referenceCode === cart.referenceCode;
        if ((contentChanged && sameReference) || (!contentChanged && !sameReference)) {
          throw new CartApiError(
            'The save response could not be confirmed. Your draft and previous Cart have been kept.',
            'uncertain',
          );
        }
      }
      setConfirmedCart(order);
      savingRef.current = false;
      await navigate({ to: '/cart' });
    } catch (error) {
      if (
        error instanceof CartApiError &&
        ['catalogue_changed', 'unavailable', 'ambiguous', 'unpriced'].includes(error.code)
      ) {
        setApproval(null);
        await loadCatalogue();
      }
      setSavingError(
        error instanceof Error
          ? error.message
          : 'The Cart could not be saved. Your draft has been kept.',
      );
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  if (!draft.doorType || !size || !draft.lock.side || !lockHeight) {
    return null;
  }
  const image = doorPreviewImage({
    doorType: draft.doorType,
    frameColour: draft.frameColour,
    lock: draft.lock,
  });
  const drawing = (
    <ManufacturingSpecDrawing
      name={name}
      type={draft.doorType}
      size={size}
      lockSide={draft.lock.side}
      lockHeight={lockHeight}
    />
  );
  const rows = [
    { label: 'Type', value: doorCopy[draft.doorType].title },
    { label: 'Mesh/Infill', value: reviewPresentation.mesh[draft.doorType] },
    { label: 'Frame colour', value: draft.frameColour },
    { label: 'Handle & hinges', value: reviewPresentation.hardware },
    { label: 'Door height', value: `${size.height}mm` },
    { label: 'Door width', value: `${size.width}mm` },
    { label: 'Handle side', value: draft.lock.side === 'left' ? 'Left' : 'Right' },
    { label: 'Handle height', value: `${lockHeight}mm` },
    { label: 'Lock type', value: draft.doorType === 'barrier' ? 'Single Lock' : 'Triple Lock' },
  ];

  return (
    <>
      <StepProgress currentIndex={4} disabled={saving} />
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 pb-56 sm:py-10 lg:pb-10">
        <h1 className="mb-1 text-2xl font-semibold text-gray-900 sm:text-3xl">Review your door</h1>
        <p className="mb-6 text-sm text-gray-500">
          Check everything is correct before adding to cart. You can still edit any step.
        </p>
        <div className="relative mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="flex flex-col gap-3 border-b border-gray-100 bg-gray-50/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <div className="flex min-w-0 items-center gap-3">
              <span
                aria-hidden="true"
                className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-progress"
              >
                <svg
                  width="16"
                  height="22"
                  viewBox="0 0 16 22"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <rect x="2" y="1" width="12" height="20" rx="0.5" />
                  <rect x="4" y="3" width="8" height="16" />
                  <circle cx="10" cy="11" r="0.8" fill="currentColor" stroke="none" />
                </svg>
              </span>
              {editingName ? (
                <input
                  autoFocus
                  aria-label="Door name"
                  data-name="review-door-name"
                  disabled={saving}
                  value={name}
                  onChange={(event) => setReviewInput('reviewName', event.target.value)}
                  onBlur={commitName}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.currentTarget.blur();
                    } else if (event.key === 'Escape') {
                      setReviewInput('reviewName', previousName.current);
                      setEditingName(false);
                    }
                  }}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm font-semibold text-gray-800 focus-visible:outline-2 focus-visible:outline-progress"
                />
              ) : (
                <span className="truncate text-sm font-semibold text-gray-800 sm:text-base">
                  {name}
                </span>
              )}
              <button
                type="button"
                data-name="review-rename"
                aria-label="Rename this door"
                disabled={saving}
                onClick={() => {
                  previousName.current = name;
                  setEditingName(true);
                }}
                className="min-h-11 px-2 text-gray-400 hover:text-progress disabled:opacity-40"
              >
                <svg
                  aria-hidden="true"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z" />
                </svg>
              </button>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <div className="flex items-center gap-1 rounded-xl border border-gray-200 bg-white pl-2.5">
                <label htmlFor="review-quantity" className="text-xs text-gray-500">
                  Qty
                </label>
                <button
                  type="button"
                  data-name="review-quantity-decrease"
                  aria-label="Decrease quantity"
                  disabled={saving || !quantityValid || quantityNumber <= doorRules.quantity.min}
                  onClick={() => changeQuantity(-1)}
                  className="h-10 w-9 text-gray-500 disabled:opacity-40"
                >
                  ‹
                </button>
                <input
                  id="review-quantity"
                  data-name="review-quantity"
                  inputMode="numeric"
                  disabled={saving}
                  value={quantity}
                  onChange={(event) => setReviewInput('reviewQuantity', event.target.value)}
                  className="w-8 text-center text-sm font-semibold text-gray-800"
                />
                <button
                  type="button"
                  data-name="review-quantity-increase"
                  aria-label="Increase quantity"
                  disabled={saving || !quantityValid || quantityNumber >= doorRules.quantity.max}
                  onClick={() => changeQuantity(1)}
                  className="h-10 w-9 text-gray-500 disabled:opacity-40"
                >
                  ›
                </button>
              </div>
              <span className="text-base font-bold text-progress sm:text-lg">
                {linePrice === undefined ? '—' : formatPrice(linePrice)}
              </span>
              {!saving && (
                <Link
                  to="/planner/measure"
                  data-name="review-edit"
                  className="inline-flex min-h-10 items-center rounded-xl border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-600 hover:text-progress"
                >
                  Edit
                </Link>
              )}
            </div>
          </div>
          <div className="p-4 sm:p-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <figure className="flex flex-col rounded-xl border border-gray-100 bg-page p-4">
                <div className="flex h-[300px] items-center justify-center overflow-hidden md:h-[425px]">
                  <img
                    data-name="review-door-image"
                    src={image.src}
                    alt={image.alt}
                    style={{ transform: image.mirrored ? 'scaleX(-1)' : undefined }}
                    className="h-full max-w-full object-contain"
                  />
                </div>
              </figure>
              <figure className="flex flex-col rounded-xl border border-gray-100 bg-white p-4">
                <LockDialog
                  name="review-drawing"
                  title={`${name} — Final Door Dimensions`}
                  triggerName="review-drawing-enlarge"
                  triggerLabel={`${name} — open larger dimensioned drawing`}
                  triggerClassName="relative flex w-full flex-col items-center justify-center rounded-lg focus-visible:outline-2 focus-visible:outline-progress"
                  trigger={
                    <>
                      <span className="flex h-[300px] w-full items-center justify-center overflow-hidden md:h-[425px]">
                        {drawing}
                      </span>
                      <EnlargeChip />
                    </>
                  }
                  disabled={saving}
                  wide
                >
                  {() => (
                    <div className="mt-4 flex h-[min(70dvh,650px)] justify-center">{drawing}</div>
                  )}
                </LockDialog>
              </figure>
            </div>
            <p className="mt-4 mb-2 text-xs font-semibold text-gray-800">
              Door we&apos;ll make — finished dimensions
            </p>
            <dl className="grid grid-cols-1 overflow-hidden rounded-xl border border-gray-200 sm:grid-flow-col sm:grid-cols-2 sm:grid-rows-5">
              {rows.map((row) => (
                <div
                  key={row.label}
                  className="flex items-baseline justify-between gap-4 border-b border-gray-100 px-3.5 py-2.5 last:border-b-0 sm:py-3"
                >
                  <dt className="text-sm whitespace-nowrap text-gray-500">{row.label}</dt>
                  <dd className="text-right text-sm font-semibold break-words text-gray-800">
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
            {sizeChange && (
              <div role="alert" className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3">
                <p className="text-sm text-gray-800">
                  The saved finished size was {sizeChange.previous.width} ×{' '}
                  {sizeChange.previous.height} mm. The reviewed finished size is{' '}
                  {sizeChange.reviewed.width} × {sizeChange.reviewed.height} mm. Remeasure or
                  confirm these finished dimensions before continuing.
                </p>
                {sizeConfirmed ? (
                  <p className="mt-2 text-sm font-semibold">Updated finished size confirmed.</p>
                ) : (
                  <button
                    type="button"
                    data-name="review-confirm-historical-size"
                    disabled={saving}
                    onClick={() => {
                      setConfirmedSizeKey(sizeKey);
                      setApproval(null);
                    }}
                    className="mt-2 min-h-11 text-sm font-semibold text-progress underline"
                  >
                    Confirm {sizeChange.reviewed.width} × {sizeChange.reviewed.height} mm
                  </button>
                )}
              </div>
            )}
            <label
              className={`mt-4 flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 ${approved ? 'border-progress bg-brand-soft/30' : 'border-gray-200 bg-page'}`}
            >
              <input
                type="checkbox"
                ref={approvalInput}
                data-name="review-drawing-approval"
                checked={approved}
                disabled={!door || !cartReady || !sizeConfirmed || editingName || saving}
                onChange={(event) =>
                  setApproval(
                    event.target.checked && door
                      ? createDrawingApproval(door, new Date().toISOString())
                      : null,
                  )
                }
                className="mt-0.5 size-6 shrink-0 accent-progress"
              />
              <span className="text-sm leading-relaxed text-gray-700">
                {drawingDeclaration.text}
              </span>
            </label>
            {!catalogue && !catalogueError && (
              <p role="status" className="mt-3 text-sm text-gray-600">
                Checking catalogue availability and price…
              </p>
            )}
            {!cartReady && !cartError && (
              <p role="status" className="mt-3 text-sm text-gray-600">
                Loading your saved Cart…
              </p>
            )}
            {catalogueError && (
              <p role="alert" className="mt-3 text-sm text-red-700">
                {catalogueError}{' '}
                <button
                  type="button"
                  data-name="review-catalogue-retry"
                  onClick={() => void loadCatalogue()}
                  className="font-semibold underline"
                >
                  Retry
                </button>
              </p>
            )}
            {prepared?.status === 'invalid' && draft.inputs.reviewLineId && (
              <p role="alert" className="mt-3 text-sm text-red-700">
                {prepared.message}
              </p>
            )}
            {cartError && (
              <p role="alert" className="mt-3 text-sm text-red-700">
                {cartError}{' '}
                <button
                  type="button"
                  data-name="review-cart-retry"
                  onClick={() => void loadCart()}
                  className="font-semibold underline"
                >
                  Retry
                </button>
              </p>
            )}
            {cartInvalid && (
              <p role="alert" className="mt-3 text-sm text-red-700">
                The saved Cart contains invalid or ambiguous doors
                {existingDoors?.length
                  ? `: ${existingDoors.map((item) => item.doorName || 'Unnamed door').join(', ')}`
                  : ''}
                . These doors must be corrected before saving.
              </p>
            )}
            {!cartInvalid && (invalidRetained?.length ?? 0) > 0 && (
              <p role="alert" className="mt-3 text-sm text-red-700">
                These saved doors still need correction before output:{' '}
                {invalidRetained?.map((item) => item.doorName || 'Unnamed door').join(', ')}.
              </p>
            )}
            {cart && cart.status !== 'draft' && (
              <p role="alert" className="mt-3 text-sm text-red-700">
                This Cart is read-only. An editable copy is required.
              </p>
            )}
            {atCapacity && (
              <p role="alert" className="mt-3 text-sm text-red-700">
                A Cart can contain at most {doorRules.maxDoorsPerOrder} doors.
              </p>
            )}
            {orderingUnavailable && (
              <p role="status" className="mt-3 text-sm text-gray-600">
                Ordering is not available yet.
              </p>
            )}
            {savingError && (
              <p role="alert" className="mt-3 text-sm text-red-700">
                {savingError}
              </p>
            )}
          </div>
        </div>
        <details className="mb-3 rounded-xl border border-gray-200 bg-white">
          <summary
            data-name="review-drawing-guide"
            className="min-h-11 cursor-pointer px-4 py-3 text-sm font-semibold text-gray-800"
          >
            How to read your drawing
          </summary>
          <p className="border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
            The drawing shows the finished door dimensions, hinge positions and handle height
            measured from the bottom of the door. Check the handle side from outside your home.
          </p>
        </details>
        <div className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-white">
          <details className="group">
            <summary
              data-name="review-whats-included"
              className="flex min-h-11 cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-semibold text-gray-700 [&::-webkit-details-marker]:hidden"
            >
              <svg
                aria-hidden="true"
                className="size-4 shrink-0 text-progress"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m7.5 4.27 9 5.15M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
                <path d="m3.3 7 8.7 5 8.7-5M12 22V12" />
              </svg>
              What&apos;s Included
              <AccordionChevron />
            </summary>
            <ul className="list-disc space-y-1 border-t border-gray-100 px-8 py-3 text-sm text-gray-600">
              {doorCopy[draft.doorType].included.map((item, index, list) => (
                <li key={item}>
                  {item}
                  {index === list.length - 1 && (
                    <>
                      {' '}
                      <InfoTip name="review-cylinder-info" label="What's a 5-pin Euro cylinder?">
                        {reviewPresentation.cylinderHelp}
                      </InfoTip>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </details>
          <details className="group border-t border-gray-100">
            <summary
              data-name="review-installation-info"
              className="flex min-h-11 cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-semibold text-gray-700 [&::-webkit-details-marker]:hidden"
            >
              <svg
                aria-hidden="true"
                className="size-4 shrink-0 text-progress"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                <path d="M14 2v6h6M8 13h8M8 17h8M8 9h2" />
              </svg>
              Installation Information
              <AccordionChevron />
            </summary>
            <div className="space-y-3 border-t border-gray-100 px-4 py-3 text-xs text-gray-600">
              <p className="font-semibold">Tools required</p>
              <ul className="list-disc space-y-1.5 pl-5">
                {doorSharedCopy.toolsList.map((tool) => (
                  <li key={tool}>{tool}</li>
                ))}
              </ul>
              <p className="italic">Note: {doorSharedCopy.wallFixingsNote}</p>
              {doorSharedCopy.urls.installationGuide ? (
                <a
                  data-name="review-installation-guide"
                  href={doorSharedCopy.urls.installationGuide}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-11 items-center rounded-xl border border-gray-200 px-4 font-medium"
                >
                  Installation Guide (PDF)
                </a>
              ) : (
                <div>
                  <button
                    type="button"
                    data-name="review-installation-guide"
                    disabled
                    className="min-h-11 rounded-xl border border-gray-200 px-4 font-medium text-gray-400"
                  >
                    Installation Guide (PDF)
                  </button>
                  <p className="mt-1">Installation guide coming soon</p>
                </div>
              )}
            </div>
          </details>
        </div>
        <div className="mb-6 flex flex-col gap-2 rounded-2xl bg-gray-900 px-5 py-4 text-white sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5">
          <div className="sm:pr-6">
            <p className="text-sm font-semibold">Total Estimated Price</p>
            <p className="mt-1 text-xs leading-relaxed text-white/70">
              GST included. {reviewPresentation.finalPriceNotice}
            </p>
          </div>
          <span className="text-2xl font-bold whitespace-nowrap text-progress sm:text-3xl">
            {linePrice === undefined ? '—' : formatPrice(linePrice)}
          </span>
        </div>
        <nav
          aria-label="Review navigation"
          className="fixed inset-x-0 bottom-0 z-30 flex flex-col gap-2 border-t border-gray-200 bg-white px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:static lg:mt-8 lg:border-0 lg:bg-transparent lg:p-0"
        >
          {!approved &&
            door &&
            cartReady &&
            sizeConfirmed &&
            !cartInvalid &&
            !atCapacity &&
            !orderingUnavailable &&
            (!cart || cart.status === 'draft') &&
            !editingName &&
            !saving &&
            !savingError && (
              <button
                type="button"
                data-name="review-approval-hint"
                onClick={() => {
                  approvalInput.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  approvalInput.current?.focus();
                }}
                className="text-right text-xs font-medium text-progress"
              >
                Check the drawing approval box to continue.
              </button>
            )}
          <div className="flex justify-between gap-4">
            {saving ? (
              <button
                type="button"
                disabled
                data-name="step-back"
                className="rounded-xl border border-gray-200 px-5 py-3 text-sm font-semibold text-gray-400"
              >
                <span aria-hidden="true">← </span>Back
              </button>
            ) : (
              <Link
                to="/planner/colour"
                data-name="step-back"
                className="rounded-xl border border-gray-200 bg-white px-5 py-3 text-sm font-semibold text-gray-700"
              >
                <span aria-hidden="true">← </span>Back
              </Link>
            )}
            <button
              type="button"
              data-name="review-add-to-cart"
              disabled={!canSave}
              onClick={() => void save()}
              className="rounded-xl bg-progress px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              {saving ? (
                'Saving…'
              ) : (
                <>
                  Add to cart <span aria-hidden="true">→</span>
                </>
              )}
            </button>
          </div>
        </nav>
      </main>
    </>
  );
}

function AccordionChevron() {
  return (
    <svg
      aria-hidden="true"
      className="ml-auto size-4 shrink-0 text-gray-400 transition-transform duration-200 group-open:rotate-180"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}
